import { useForm, Control } from 'react-hook-form';
import { isCleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Lock } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

export interface RoleFormData {
  role_key: string;
  role_name: string;
  role_description: string;
}

interface RoleFormProps {
  initialData?: Partial<RoleFormData>;
  onSubmit: (data: RoleFormData) => void;
  isSubmitting: boolean;
  isEditing?: boolean;
  onCancel: () => void;
}

// With yupResolver the Input/Textarea clean-text rule is skipped, so the schema keeps the check
const roleSchema = yup.object({
  role_key: yup
    .string()
    .required('Role key is required')
    .matches(/^[A-Z_]+$/, 'Role key must contain only uppercase letters and underscores'),
  role_name: yup.string().required('Role name is required').trim()
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  role_description: yup.string().default('')
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v))
}).required();

export default function RoleForm({
  initialData,
  onSubmit,
  isSubmitting,
  isEditing = false,
  onCancel
}: RoleFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control
  } = useForm<RoleFormData>({
    resolver: yupResolver(roleSchema) as any,
    // Validate while typing so the clean-text error shows under the field, not only after Save
    mode: 'onChange',
    defaultValues: {
      role_key: initialData?.role_key || '',
      role_name: initialData?.role_name || '',
      role_description: initialData?.role_description || ''
    }
  });
  const formControl = control as Control<RoleFormData>;

  const handleRoleKeyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z_]/g, '');
    setValue('role_key', value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Role Key <span className="text-red-500">*</span>
        </label>
        {isEditing ? (
          <div className="relative">
            <input
              type="text"
              value={watch('role_key')}
              className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              disabled
            />
            <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        ) : (
          <input
            type="text"
            {...register('role_key')}
            onChange={handleRoleKeyInput}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.role_key ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., MANAGER or TEAM_LEAD"
          />
        )}
        {errors.role_key && (
          <p className="text-red-500 text-sm mt-1">{errors.role_key.message}</p>
        )}
        {!isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Only uppercase letters (A-Z) and underscores (_) allowed
          </p>
        )}
        {isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Role key cannot be changed after creation
          </p>
        )}
      </div>

      <Input<RoleFormData>
        name="role_name"
        control={formControl}
        label="Role Name"
        required
        type="text"
        placeholder="e.g., Manager"
      />

      <Textarea<RoleFormData>
        name="role_description"
        control={formControl}
        label="Description"
        rows={3}
        placeholder="Enter role description..."
      />

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          disabled={isSubmitting}
        >
          {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Role' : 'Create Role')}
        </button>
      </div>
    </form>
  );
}